import { message } from 'antd'
import { useEffect, useRef } from 'react'
import { MyForm } from '~/components/ui/MyForm'
import { GET_AI_TOTAL_INFO, GET_AI_MEMBER_LIST } from '~/graphqls/gqls'
import {
  AiCreateArtworkModel,
  AiCreateArtworkPattern,
  AiArtworkOptionInput,
  AiArtworkModelFusionInput,
  SuperType,
  PretreatmentMethod,
  QueueType,
  getAITotalInfo,
  getAIMemberList,
  getAIMemberListVariables,
  getAITotalInfo_pictureGraph_aiArtworkTotalInfo,
} from '~/graphqls/types'

import { useImperativeQuery } from '~/hooks/useImperativeQuery'
import styles from './AiForm.module.css'
import { clearStorage, getStorage } from '~/utils'
import { observer } from 'mobx-react'
import { useStores } from '~/hooks/useStores'
import Router from 'next/router'
import useOptions from './ai-form/useOptions'
import { controlnetPatternArr, isControlnetPattern } from '~/configs/ai'
import { PromptComp } from './ai-form/PromptComp'
import { StyleModelComp } from './ai-form/StyleModelComp'
import { GenerationComp } from './ai-form/GenerationComp'
import { NormalInitImageComp } from './ai-form/NormalInitImageComp'
import { LabInitImageComp } from './ai-form/LabInitImageComp'
import { PreProcessComp } from './ai-form/PreProcessComp'
import { PreProcessSwitchComp } from './ai-form/PreProcessSwitchComp'
import { LabModelCodeComp } from './ai-form/LabModelCodeComp'
import { ModelSelectComp } from './ai-form/ModelSelectComp'
import { PrefineComp } from './ai-form/PrefineComp'
import { MaskImageComp } from './ai-form/MaskImageComp'
import { PictureSizeComp } from './ai-form/PictureSizeComp'
import { PictureResolutionComp } from './ai-form/PictureResolutionComp'
import { ModelThemeComp } from './ai-form/ModelThemeComp'
import { StyleDecorationComp } from './ai-form/StyleDecorationComp'
import { AdvanceComp } from './ai-form/AdvanceComp'
import { UcPropmtComp } from './ai-form/UcPropmtComp'
import { CharacterComp } from './ai-form/CharacterComp'
import { ModelFusionComp } from './ai-form/ModelFusionComp'
import { CreativityDegreeComp } from './ai-form/CreativityDegreeComp'
import { PaintTimesComp } from './ai-form/PaintTimesComp'
import { AgreementComp } from './ai-form/AgreementComp'
import { useAtom } from 'jotai'
import {
  creativityDegreeAtom,
  doRepairTheHandAtom,
  hasAdvanceAtom,
  maskImageUrlAtom,
  modelFusionAtom,
  optionsAtom,
  paintTimesAtom,
  patternAtom,
  seedAtom,
  selectModelAtom,
  selectResolutionAtom,
  selectSizeAtom,
  styleDecorationAtom,
  selectSamplerAtom,
  initImageUrlAtom,
  characterAtom,
  ucPromptAtom,
  promptAtom,
  cfgAtom,
  pretreatmentMethodAtom,
  controlPrefineAtom,
  gptContentAtom,
  trackEventLabelAtom,
  modelCodeAtom,
  styleModelAtom,
  genNightAtom,
  ensdAtom,
  clipSkipAtom,
  totalInfoAtom,
  memberListAtom,
  resolutionKeyAtom,
  DEFAULT_OPTIONS,
} from './ai-form/atom'
import { VipParamsComp } from './ai-form/VipParamsComp'
import { CreateComp } from './ai-form/CreateComp'
import { useForm } from './ai-form/useForm'

export interface FormParamsProps {
  prompt: string
  size: string
  width: number
  length: number
  model?: AiCreateArtworkModel
  styleModel?: string | null
  initImageUrl?: string
  creativityDegree?: number
  num?: number
  maskImageUrl?: string
  maskZoneImageUrl?: string
  pattern: AiCreateArtworkPattern
  modelCode: number
  ucPrompt?: string
  doRepairTheHand?: boolean
  styleDecorationOptions?: AiArtworkOptionInput[]
  character?: string[]
  modelFusion?: AiArtworkModelFusionInput[]
  initWidth?: number
  initHeight?: number
  superType?: SuperType | null
  cfg?: number | null
  seed?: string | null
  samplerIndex?: number | null
  steps?: number | null
  /** 线稿上色模式特有参数：预处理方式 */
  pretreatmentMethod?: PretreatmentMethod
  /** 百度统计事件 */
  trackEventLabel?: string | null
  isGPT?: boolean
  ensd?: number
  clipSkip?: number
  queueType?: QueueType
}

export const AiForm = observer(() => {
  const { store } = useStores()
  const [options] = useAtom(optionsAtom)
  const [, setTotalInfo] = useAtom(totalInfoAtom)
  const [, setMemberList] = useAtom(memberListAtom)
  const [selectModel, setSelectModel] = useAtom(selectModelAtom)
  const [modelCode, setModelCode] = useAtom(modelCodeAtom)
  const [pattern] = useAtom(patternAtom)
  /** 创意度 */
  const [creativityDegree, setCreativityDegree] = useAtom(creativityDegreeAtom)
  /** 融合模型 */
  const [modelFusion, setModelFusion] = useAtom(modelFusionAtom)
  const [, setResolutionKey] = useAtom(resolutionKeyAtom)
  /** 画面大小 */
  const [selectSize, setSelectSize] = useAtom(selectSizeAtom)
  /** 画面尺寸 */
  const [selectResolution, setSelectResolution] = useAtom(selectResolutionAtom)
  /** 风格选择 */
  const [styleDecoration, setStyleDecoration] = useAtom(styleDecorationAtom)
  /** 高级设置 */
  const [hasAdvance, setHasAdvance] = useAtom(hasAdvanceAtom)
  /** 作画数量 */
  const [paintTimes, setPaintTimes] = useAtom(paintTimesAtom)
  /** 涂抹图片URL */
  const [, setMaskImageUrl] = useAtom(maskImageUrlAtom)
  /** 修手 */
  const [doRepairTheHand, setDoRepairTheHand] = useAtom(doRepairTheHandAtom)
  /** 随机种子 */
  const [seed] = useAtom(seedAtom)
  /** 采样模式 */
  const [selectSampler, setSelectSampler] = useAtom(selectSamplerAtom)
  /** 底图 */
  const [initImageUrl, setInitImageUrl] = useAtom(initImageUrlAtom)
  /** 角色同人 */
  const [character, setCharacter] = useAtom(characterAtom)
  const [ucPrompt, setUcPrompt] = useAtom(ucPromptAtom)
  const [prompt, setPrompt] = useAtom(promptAtom)
  const [gptContent, setGptContent] = useAtom(gptContentAtom)
  const [trackEventLabel, setTrackEventLabel] = useAtom(trackEventLabelAtom)
  const [cfg, setCfg] = useAtom(cfgAtom)
  const [styleModel, setStyleModel] = useAtom(styleModelAtom)
  const [ensd, setEnsd] = useAtom(ensdAtom)
  const [clipSkip, setClipSkip] = useAtom(clipSkipAtom)
  /** 预处理 */
  const [pretreatmentMethod, setPretreatmentMethod] = useAtom(
    pretreatmentMethodAtom
  )
  const [selectedControlPrefine, setSelectedControlPrefine] =
    useAtom(controlPrefineAtom)
  // 夜间生成
  const [genNight, setGenNight] = useAtom(genNightAtom)

  const [form] = MyForm.useForm()

  /** 涂抹模式专用 */
  const paintRef = useRef<any>()

  /** controlnet模式 */
  const isControlnet = isControlnetPattern(pattern)
  /** 风格模型 */
  const isST = selectModel === AiCreateArtworkModel.STYLE
  /** 涂抹模式 */
  const isMASK = pattern === AiCreateArtworkPattern.MASK
  /** 是否使用chatgpt */
  const isGPT = !!gptContent && prompt === gptContent

  const { isSupportLoRA } = useOptions()
  const { getBestSizeForImage, setPrefineOptions } = useForm()

  const getTotalInfoAction =
    useImperativeQuery<getAITotalInfo>(GET_AI_TOTAL_INFO)

  const getMemberListAction = useImperativeQuery<
    getAIMemberList,
    getAIMemberListVariables
  >(GET_AI_MEMBER_LIST)

  useEffect(() => {
    /** 作画时间等 */
    if (store.ai?.isRefreshTotalInfo) {
      getTotalInfoAction()
        .then(res => {
          setTotalInfo(
            (res.data.pictureGraph
              ?.aiArtworkTotalInfo as getAITotalInfo_pictureGraph_aiArtworkTotalInfo) ||
              {}
          )
        })
        .catch(err => {
          message.destroy()
          message.error('更新AI作画信息失败，请刷新')
        })
        .finally(() => {
          store.ai?.setIsRefreshTotalInfo(false)
        })
    }
  }, [store.ai?.isRefreshTotalInfo])

  useEffect(() => {
    /** 权益卡 */
    if (store.ai?.isRefreshMemberList) {
      getMemberListAction()
        .then(res => {
          setMemberList(res.data.accountModule?.userMemberCardList || [])
        })
        .catch(err => {
          console.error(err)
        })
        .finally(() => {
          store.ai?.setIsRefreshMemberList(false)
        })
    }
  }, [store.ai?.isRefreshMemberList])

  /** 再来一张 */
  const again = async () => {
    const aiParams: any = getStorage('aiParam')

    console.log(aiParams, 'aiParams')

    if (!Object.keys(aiParams).length) return

    // 如果在涂抹模式下，将当前图带入待涂抹的图, 同时清空prompt
    if (pattern === AiCreateArtworkPattern.MASK) {
      setPrompt('')
      setMaskImageUrl(aiParams.originUrl)
    } else {
      setPrompt(aiParams.prompt)
      // 神奇涂抹不需要，因为有lockModelCode
      setModelCode(aiParams.modelCode)
    }

    // 手机照改漫Tab生成的图片没有size，根据底图为其匹配size
    if (!aiParams.size) {
      let size = (await getBestSizeForImage(
        aiParams.initImageUrl ?? aiParams.originUrl
      )) as string

      const curResolution =
        options?.allSizeOptions?.[0]?.sizeSetOptions?.find(
          item => item?.name === size
        )?.resolutionList || []

      setSelectSize(size)
      setSelectResolution(
        `${curResolution?.[0]?.length}*${curResolution?.[0]?.width}*${curResolution?.[0]?.superType}`
      )
      setResolutionKey(options?.allSizeOptions?.[0]?.resolutionKey!)
    } else {
      setSelectSize(aiParams.size)
      setSelectResolution(aiParams.resolution)
      setResolutionKey(aiParams.resolutionKey)
    }

    if (isControlnet) {
      setPrefineOptions({
        width: Number(aiParams.resolution.split('*')[0] || 0),
        height: Number(aiParams.resolution.split('*')[1] || 0),
      })
    }

    setSelectModel(aiParams.model)
    setStyleModel(aiParams.styleModel)
    setSelectedControlPrefine(aiParams.superType)
    setPretreatmentMethod(aiParams.pretreatmentMethod)
    setStyleDecoration(aiParams.styleDecorationOptions || [])

    // 备注：底图不能是空字符串
    setInitImageUrl(aiParams.initImageUrl || undefined)
    setCreativityDegree(
      aiParams.creativityDegree ?? DEFAULT_OPTIONS.CREATIVITY_DEGREE
    )
    setUcPrompt(aiParams.ucPrompt)
    setCharacter(aiParams.character)
    setModelFusion(aiParams.modelFusion || [])
    setSelectSampler(
      typeof aiParams?.samplerModelInfo?.samplerIndex === 'number'
        ? [aiParams?.samplerModelInfo?.samplerIndex]
        : []
    )
    setCfg(aiParams.cfg)
    setClipSkip(aiParams.clipSkip)
    setEnsd(aiParams.ensd)

    /** 修手 */
    setDoRepairTheHand(!!aiParams.doRepairTheHand)
    setTrackEventLabel(aiParams.trackEventLabel)

    setHasAdvance(
      (pattern === AiCreateArtworkPattern.GENERAL
        ? !!aiParams.initImageUrl
        : false) ||
        !!aiParams.modelFusion?.length ||
        !!aiParams.character?.length ||
        !!aiParams.cfg ||
        !!aiParams.ensd ||
        !!aiParams.clipSkip ||
        !!aiParams.seed ||
        !!aiParams.ucPrompt ||
        !!aiParams.samplerModelInfo
    )

    clearStorage('aiParam')
    store.ai?.setIsAgain(false)
  }

  /** 来自别的页面的再来一张 */
  useEffect(() => {
    again()
  }, [])

  /** 来自本页面的再来一张 */
  useEffect(() => {
    if (store.ai?.isAgain) {
      again()
    }
  }, [store.ai?.isAgain])

  const getParams: () => Promise<FormParamsProps> = async () => {
    let maskImageUrl
    let maskZoneImageUrl
    if (pattern === AiCreateArtworkPattern.MASK) {
      const res = await paintRef.current.save()

      if (!res?.[0]) {
        message.destroy()
        message.error('请上传图片')
        return Promise.reject()
      }

      console.log(res)

      maskImageUrl = res[0]
      maskZoneImageUrl = res[1]
    } else if (pattern !== AiCreateArtworkPattern.GENERAL && !initImageUrl) {
      message.destroy()
      message.error('请上传图片')
      return Promise.reject()
    }

    let width: number = 0
    let height: number = 0
    let superType: SuperType = SuperType.SUPERSIZE
    if (selectResolution) {
      const [a, b, s] = selectResolution.split('*')
      width = Number(a)
      height = Number(b)
      superType = s as SuperType
    }

    let size = selectSize
    let initWidth: number | undefined = undefined // 底图宽
    let initHeight: number | undefined = undefined // 底图高
    if (initImageUrl) {
      await new Promise<void>((resolve, reject) => {
        const img = new Image()
        img.src = initImageUrl
        img.onload = () => {
          initWidth = img.naturalWidth
          initHeight = img.naturalHeight
          resolve()
        }
        img.onerror = () => {
          message.destroy()
          message.error('获取底图宽度时出错')
          reject()
        }
      })
    }

    /** controlnet 没有尺寸，用底图尺寸处理 size resolution */
    if (isControlnet) {
      size = `${initWidth}:${initHeight}`
      width = initWidth!
      height = initHeight!
      superType = selectedControlPrefine as SuperType
    }

    return {
      character: isSupportLoRA && !isST ? character : [],
      modelFusion: isSupportLoRA && !isST ? modelFusion : [],
      styleDecorationOptions: !isST ? styleDecoration : [],
      prompt: prompt || '',
      size: size,
      width: height, // 高
      length: width, // 宽
      model: selectModel,
      styleModel: isST ? styleModel : undefined,
      superType,
      initImageUrl: !isST && initImageUrl ? initImageUrl : undefined,
      initWidth: !isST && initImageUrl ? initWidth : undefined,
      initHeight: !isST && initImageUrl ? initHeight : undefined,
      creativityDegree: !isST ? creativityDegree : undefined,
      num: paintTimes,
      maskImageUrl: !isST ? maskImageUrl : undefined,
      maskZoneImageUrl: !isST ? maskZoneImageUrl : undefined,
      pattern,
      modelCode: modelCode!,
      ucPrompt: !isST ? ucPrompt : undefined,
      doRepairTheHand: !isST ? doRepairTheHand : false,
      cfg: hasAdvance && !isST ? cfg : undefined,
      ensd: hasAdvance && !isST ? ensd : undefined,
      clipSkip: hasAdvance && !isST ? clipSkip : undefined,
      seed: hasAdvance && !isST ? seed : undefined,
      samplerIndex: hasAdvance && !isST ? selectSampler?.[0] : undefined,
      pretreatmentMethod: !isST ? pretreatmentMethod : undefined,
      trackEventLabel,
      isGPT,
      queueType: genNight ? QueueType.NIGHT : QueueType.NORMAL,
    }
  }

  const reset = () => {
    setPaintTimes(1)
    setGptContent('')
    setGenNight(false)
  }

  return (
    <>
      <MyForm layout="vertical" form={form} $dark className={styles.darkForm}>
        {[AiCreateArtworkPattern.DEPTH2IMG, ...controlnetPatternArr].includes(
          pattern
        ) && <LabInitImageComp />}

        {/* scribble 预处理方式 */}
        {[AiCreateArtworkPattern.SCRIBBLE].includes(pattern) && (
          <PreProcessComp />
        )}

        {/* controlnet 除 scribble 外的预处理方式 */}
        {pattern !== AiCreateArtworkPattern.SCRIBBLE && isControlnet && (
          <PreProcessSwitchComp />
        )}

        {/* 模型主题 */}
        {isControlnet && <LabModelCodeComp />}

        {/* dd sd ad选择 */}
        {[AiCreateArtworkPattern.GENERAL].includes(pattern) && (
          <ModelSelectComp />
        )}

        {/* 画面描述 */}
        {[
          AiCreateArtworkPattern.GENERAL,
          AiCreateArtworkPattern.DEPTH2IMG,
          ...controlnetPatternArr,
        ].includes(pattern) && (
          <PromptComp
            toTagGen={() => {
              Router.push(`/tag-generator?model=${selectModel}`)
            }}
            pattern={pattern}
            modelCode={modelCode}
          />
        )}

        {/** 图像精绘 */}
        {isControlnet && <PrefineComp />}

        {/** 涂抹 */}
        {pattern === AiCreateArtworkPattern.MASK && (
          <>
            <MaskImageComp paintRef={paintRef} />
            {/* 2023-3-24 chatgpt */}
            <PromptComp pattern={AiCreateArtworkPattern.MASK} />
          </>
        )}

        {/* 画面大小和尺寸 */}
        {!isControlnet && (
          <>
            <PictureSizeComp />
            <PictureResolutionComp />
          </>
        )}

        {/* 风格模型 */}
        {isST && <StyleModelComp />}

        {/* 模型主题 */}
        {pattern === AiCreateArtworkPattern.GENERAL && !isST && (
          <ModelThemeComp />
        )}

        {/** 风格选择 */}
        {[AiCreateArtworkPattern.GENERAL, AiCreateArtworkPattern.MASK].includes(
          pattern
        ) &&
          !isST && <StyleDecorationComp />}

        {/** 高级设置 */}
        {!isMASK && !isST && <AdvanceComp />}

        {hasAdvance && !isST && (
          <>
            {pattern === AiCreateArtworkPattern.GENERAL && (
              /** 底图 */
              <NormalInitImageComp />
            )}

            {/* 负面描述 */}
            {[
              AiCreateArtworkPattern.DEPTH2IMG,
              ...controlnetPatternArr,
              AiCreateArtworkPattern.GENERAL,
            ].includes(pattern) && <UcPropmtComp />}

            {/** 角色同人 */}
            {isSupportLoRA &&
              [
                ...controlnetPatternArr,
                AiCreateArtworkPattern.GENERAL,
              ].includes(pattern) && <CharacterComp />}

            {/** 融合模型 */}
            {isSupportLoRA &&
              [
                ...controlnetPatternArr,
                AiCreateArtworkPattern.GENERAL,
              ].includes(pattern) && <ModelFusionComp />}

            {/* 创意度 */}
            {[
              AiCreateArtworkPattern.DEPTH2IMG,
              ...controlnetPatternArr,
            ].includes(pattern) && <CreativityDegreeComp />}

            {/* 会员专享参数 */}
            {[AiCreateArtworkPattern.GENERAL, ...controlnetPatternArr].includes(
              pattern
            ) && <VipParamsComp />}
          </>
        )}
        {/* 作画次数 */}
        <PaintTimesComp />

        {/* 生成方式 */}
        <GenerationComp />

        {/* 协议 */}
        <AgreementComp />
      </MyForm>

      {/* AI生成 */}
      <CreateComp reset={reset} getParams={getParams} />
    </>
  )
})
